import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TvIcon from '@mui/icons-material/Tv';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { adminApi, type UpdateCompetitionDto } from '../../api/adminApi';
import type {
  BoardLayout,
  BoardSortMode,
  Competition,
  PublicResultsLayout,
  PublicThemeMode,
} from '../../types/domain';
import {
  BOARD_LAYOUT_LABELS,
  BOARD_LAYOUT_DESCRIPTIONS,
  BOARD_SORT_MODE_LABELS,
  BOARD_THEME_LABELS,
  PUBLIC_LAYOUT_LABELS,
  PUBLIC_LAYOUT_DESCRIPTIONS,
  PUBLIC_THEME_LABELS,
} from '../../types/domain';
import { LayoutPreview } from '../../components/results/LayoutPreview';
import { BoardLayoutPreview } from '../../components/results/BoardLayoutPreview';
import { LayoutPreviewDialog } from '../../components/admin/LayoutPreviewDialog';
import { useLayoutVisibility } from '../../hooks/useLayoutVisibility';

interface Props {
  competition: Competition;
}

const LabelWithHelp = ({ label, help }: { label: string; help?: string }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" component="span">
    <span>{label}</span>
    {help && (
      <Tooltip title={help} placement="top" arrow enterTouchDelay={0} leaveTouchDelay={4000}>
        <HelpOutlineIcon
          sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }}
        />
      </Tooltip>
    )}
  </Stack>
);

const LAYOUTS: PublicResultsLayout[] = ['CLASSIC', 'PODIUM', 'SPLIT_VIEW'];
const THEMES: PublicThemeMode[] = ['AUTO', 'LIGHT', 'DARK'];
const BOARD_LAYOUTS: BoardLayout[] = ['MATRIX', 'FOCUS', 'CAROUSEL', 'SPLIT', 'COLUMNS'];

export const CompetitionDisplaySettingsTab = ({ competition }: Props) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: visibility } = useLayoutVisibility();

  const visiblePublicLayouts = useMemo<PublicResultsLayout[]>(
    () => LAYOUTS.filter((l) => visibility?.publicLayouts[l] ?? true),
    [visibility],
  );
  const visibleBoardLayouts = useMemo<BoardLayout[]>(
    () => BOARD_LAYOUTS.filter((l) => visibility?.boardLayouts[l] ?? true),
    [visibility],
  );

  const publicDefault = visibility?.publicDefault ?? 'CLASSIC';
  const boardDefault = visibility?.boardDefault ?? 'MATRIX';

  const resolvePublicLayout = (l: PublicResultsLayout): PublicResultsLayout => {
    if (!LAYOUTS.includes(l)) return publicDefault;
    if (visibility && visibility.publicLayouts[l] === false) return publicDefault;
    return l;
  };
  const resolveBoardLayout = (l: BoardLayout): BoardLayout => {
    if (!BOARD_LAYOUTS.includes(l)) return boardDefault;
    if (visibility && visibility.boardLayouts[l] === false) return boardDefault;
    return l;
  };

  const savedPublicHidden =
    !!visibility && LAYOUTS.includes(competition.publicResultsLayout) &&
    visibility.publicLayouts[competition.publicResultsLayout] === false;
  const savedBoardHidden =
    !!visibility && BOARD_LAYOUTS.includes(competition.boardLayout) &&
    visibility.boardLayouts[competition.boardLayout] === false;

  const buildInitial = () => ({
    publicResultsLayout: resolvePublicLayout(competition.publicResultsLayout),
    publicShowStartNumber: competition.publicShowStartNumber,
    publicShowPenaltySeconds: competition.publicShowPenaltySeconds,
    publicShowPoints: competition.publicShowPoints,
    publicShowAttemptDetails: competition.publicShowAttemptDetails,
    publicShowLaneDetails: competition.publicShowLaneDetails,
    publicShowNote: competition.publicShowNote,
    publicRotationSeconds: competition.publicRotationSeconds,
    publicRotateOverall: competition.publicRotateOverall,
    publicAutoRefreshSeconds: competition.publicAutoRefreshSeconds,
    publicThemeMode: competition.publicThemeMode,
    publicShowLogo: competition.publicShowLogo,
    publicShowOrganizerLogo: competition.publicShowOrganizerLogo,
    publicOrganizerLogoUrl: competition.publicOrganizerLogoUrl ?? '',
    publicOrganizerLogoScale: competition.publicOrganizerLogoScale ?? 100,
    boardLayout: resolveBoardLayout(competition.boardLayout),
    boardThemeMode: competition.boardThemeMode,
    boardFontScale: competition.boardFontScale ?? 100,
    boardShowAllTeams: competition.boardShowAllTeams ?? false,
    boardSortMode: (competition.boardSortMode ?? 'RANK') as BoardSortMode,
    boardShowLaneDetails: competition.boardShowLaneDetails ?? false,
    boardScreensaverEnabled: competition.boardScreensaverEnabled ?? true,
    boardCustomText: competition.boardCustomText ?? '',
    publicDisclaimerText: competition.publicDisclaimerText ?? '',
    falseStartThreshold: String(competition.falseStartThreshold ?? 1),
  });

  const [draft, setDraft] = useState(buildInitial);
  const [previewPublic, setPreviewPublic] = useState<PublicResultsLayout | null>(null);
  const [previewBoard, setPreviewBoard] = useState<BoardLayout | null>(null);

  // Re-sync draft kdyz se parent competition zmeni (napr. po jine mutaci).
  useEffect(() => {
    setDraft(buildInitial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition.updatedAt, competition.id]);

  // Kdyz dorazi visibility pozdeji, normalizuj jen layouty v draftu (ostatni editace uzivatele necháme).
  useEffect(() => {
    if (!visibility) return;
    setDraft((d) => {
      const nextPublic = resolvePublicLayout(d.publicResultsLayout);
      const nextBoard = resolveBoardLayout(d.boardLayout);
      if (nextPublic === d.publicResultsLayout && nextBoard === d.boardLayout) return d;
      return { ...d, publicResultsLayout: nextPublic, boardLayout: nextBoard };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  const isDirty = useMemo(() => {
    return (
      draft.publicResultsLayout !== competition.publicResultsLayout ||
      draft.publicShowStartNumber !== competition.publicShowStartNumber ||
      draft.publicShowPenaltySeconds !== competition.publicShowPenaltySeconds ||
      draft.publicShowPoints !== competition.publicShowPoints ||
      draft.publicShowAttemptDetails !== competition.publicShowAttemptDetails ||
      draft.publicShowLaneDetails !== competition.publicShowLaneDetails ||
      draft.publicShowNote !== competition.publicShowNote ||
      draft.publicRotationSeconds !== competition.publicRotationSeconds ||
      draft.publicRotateOverall !== competition.publicRotateOverall ||
      draft.publicAutoRefreshSeconds !== competition.publicAutoRefreshSeconds ||
      draft.publicThemeMode !== competition.publicThemeMode ||
      draft.publicShowLogo !== competition.publicShowLogo ||
      draft.publicShowOrganizerLogo !== competition.publicShowOrganizerLogo ||
      (draft.publicOrganizerLogoUrl ?? '') !== (competition.publicOrganizerLogoUrl ?? '') ||
      draft.publicOrganizerLogoScale !== (competition.publicOrganizerLogoScale ?? 100) ||
      draft.boardLayout !== competition.boardLayout ||
      draft.boardThemeMode !== competition.boardThemeMode ||
      draft.boardFontScale !== (competition.boardFontScale ?? 100) ||
      draft.boardShowAllTeams !== (competition.boardShowAllTeams ?? false) ||
      draft.boardSortMode !== (competition.boardSortMode ?? 'RANK') ||
      draft.boardShowLaneDetails !== (competition.boardShowLaneDetails ?? false) ||
      draft.boardScreensaverEnabled !== (competition.boardScreensaverEnabled ?? true) ||
      (draft.boardCustomText ?? '') !== (competition.boardCustomText ?? '') ||
      (draft.publicDisclaimerText ?? '') !== (competition.publicDisclaimerText ?? '') ||
      Number(draft.falseStartThreshold) !== (competition.falseStartThreshold ?? 1)
    );
  }, [draft, competition]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: UpdateCompetitionDto = {
        name: competition.name,
        competitionDate: competition.competitionDate,
        location: competition.location ?? undefined,
        status: competition.status,
        tieBreakerEnabled: competition.tieBreakerEnabled,
        penaltyTrackingMode: competition.penaltyTrackingMode,
        stafetaDvojicAttempts: competition.stafetaDvojicAttempts,
        stafetaDvojicParallelTracks: competition.stafetaDvojicParallelTracks,
        npPenaltyRank: competition.npPenaltyRank,
        overallRankingVisible: competition.overallRankingVisible,
        ...draft,
        publicOrganizerLogoUrl: draft.publicOrganizerLogoUrl.trim() || null,
        // Prazdny string → smazat text; jinak poslat trimly obsah.
        boardCustomText: draft.boardCustomText.trim() === '' ? '' : draft.boardCustomText,
        publicDisclaimerText: draft.publicDisclaimerText.trim() === '' ? '' : draft.publicDisclaimerText,
        falseStartThreshold: Number(draft.falseStartThreshold) || 1,
      };
      return adminApi.updateCompetition(competition.id, payload);
    },
    onSuccess: () => {
      enqueueSnackbar('Nastavení zobrazení uloženo', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competition.id] });
      queryClient.invalidateQueries({ queryKey: ['public'] });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při ukládání', { variant: 'error' }),
  });

  const setField = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const previewLink = `/s/${competition.shortCode}?layout=${draft.publicResultsLayout.toLowerCase()}`;

  // Layout-aware viditelnost: ne vsechna nastaveni davaji smysl pro vsechny layouty.
  const publicShowsPenaltyAndNote = draft.publicResultsLayout !== 'CLASSIC';
  const boardUsesRotation = draft.boardLayout === 'FOCUS' || draft.boardLayout === 'CAROUSEL' || draft.boardLayout === 'SPLIT';
  const boardUsesRotateOverall = draft.boardLayout !== 'COLUMNS';
  const boardUsesTeamControls = draft.boardLayout !== 'COLUMNS';
  const boardUsesLaneDetails = draft.boardLayout === 'COLUMNS';
  const boardSectionVisible = boardUsesRotation || boardUsesRotateOverall;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => adminApi.uploadOrganizerLogo(competition.id, file),
    onSuccess: () => {
      enqueueSnackbar('Logo nahráno', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competition.id] });
      queryClient.invalidateQueries({ queryKey: ['public'] });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => {
      const status = e?.response?.status;
      const msg =
        status === 413
          ? 'Soubor je příliš velký (max. 3 MB)'
          : e?.response?.data?.message ?? 'Nahrání selhalo';
      enqueueSnackbar(msg, { variant: 'error' });
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => adminApi.deleteOrganizerLogo(competition.id),
    onSuccess: () => {
      enqueueSnackbar('Logo smazáno', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competition.id] });
      queryClient.invalidateQueries({ queryKey: ['public'] });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Smazání selhalo', { variant: 'error' }),
  });

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      enqueueSnackbar('Soubor přesahuje 3 MB', { variant: 'error' });
      e.target.value = '';
      return;
    }
    uploadMutation.mutate(file);
    e.target.value = '';
  };

  const hasUploadedLogo = !!competition.publicOrganizerLogoMime;
  const logoSrc = `/api/public/competitions/${competition.shortCode}/organizer-logo?v=${encodeURIComponent(
    competition.updatedAt ?? '',
  )}`;

  const boardUrl = competition.boardToken
    ? `${window.location.origin}/board/${competition.boardToken}`
    : null;

  const copyBoardUrl = () => {
    if (!boardUrl) return;
    navigator.clipboard.writeText(boardUrl).then(
      () => enqueueSnackbar('Odkaz zkopírován', { variant: 'success' }),
      () => enqueueSnackbar('Kopírování selhalo', { variant: 'error' }),
    );
  };

  return (
    <Stack spacing={4}>
      <Alert severity="info" variant="outlined">
        Nastavte layout pro veřejnou stránku (diváky) i pro fullscreen plat / televizi / projektor. Změny se projeví ihned po uložení.
      </Alert>

      {/* Board URL — neverejny odkaz pro plat */}
      {boardUrl && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.light', borderColor: 'warning.main' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TvIcon color="warning" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Odkaz pro plat / televizi / projektor
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Soukromý odkaz — neposkytujte ho veřejně. Otevře fullscreen zobrazení všech
                kategorií a disciplín + QR kód pro diváky.
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                  bgcolor: 'background.paper',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                }}
              >
                {boardUrl}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyBoardUrl}
              >
                Kopírovat
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<OpenInNewIcon />}
                href={boardUrl}
                target="_blank"
                rel="noopener"
              >
                Otevřít
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Sekce A: Layout */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          1. Layout pro diváky
        </Typography>
        {savedPublicHidden && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Původně zvolený layout je nyní skrytý superadminem — bylo nastaveno „
            {PUBLIC_LAYOUT_LABELS[publicDefault]}". Uložte změny, aby se to propsalo do soutěže.
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {visiblePublicLayouts.map((l) => {
            const selected = draft.publicResultsLayout === l;
            return (
              <Paper
                key={l}
                variant="outlined"
                onClick={() => setField('publicResultsLayout', l)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  transition: 'all 150ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <LayoutPreview layout={l} size={80} />
                <Typography fontWeight={700}>{PUBLIC_LAYOUT_LABELS[l]}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {PUBLIC_LAYOUT_DESCRIPTIONS[l]}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewPublic(l);
                  }}
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                >
                  Ukázka
                </Button>
              </Paper>
            );
          })}
        </Box>
      </Box>

      <Divider />

      {/* Sekce B: Obsah */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          2. Co zobrazit
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 0.5,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.publicShowStartNumber}
                onChange={(e) => setField('publicShowStartNumber', e.target.checked)}
              />
            }
            label="Startovní čísla týmů"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.publicShowPoints}
                onChange={(e) => setField('publicShowPoints', e.target.checked)}
              />
            }
            label="Body v celkovém pořadí"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.publicShowAttemptDetails}
                onChange={(e) => setField('publicShowAttemptDetails', e.target.checked)}
              />
            }
            label={
              <LabelWithHelp
                label="Všechny časoměřiče"
                help="Pod hlavním časem zobrazí všechny jednotlivé časy z měřičů (např. Dráha 1·1 56,25 · Dráha 1·2 56,26 · …). Bez této volby se zobrazuje jen úřední čas za dráhu."
              />
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.publicShowLaneDetails}
                onChange={(e) => setField('publicShowLaneDetails', e.target.checked)}
              />
            }
            label={
              <LabelWithHelp
                label="Čas za dráhu"
                help="Zobrazí pod hlavním časem chip s úředním časem za každou dráhu (LP/PP u požárního útoku; Dráha 1/Dráha 2 u štafety). Default zapnuto."
              />
            }
          />
          {publicShowsPenaltyAndNote && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.publicShowPenaltySeconds}
                  onChange={(e) => setField('publicShowPenaltySeconds', e.target.checked)}
                />
              }
              label="Trestné sekundy"
            />
          )}
          {publicShowsPenaltyAndNote && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.publicShowNote}
                  onChange={(e) => setField('publicShowNote', e.target.checked)}
                />
              }
              label="Poznámky k výsledkům"
            />
          )}
        </Box>
        {!publicShowsPenaltyAndNote && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            „Trestné sekundy" a „Poznámky k výsledkům" jsou dostupné pouze v layoutech Sportovní podium a Rozdělená obrazovka.
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Sekce C: Chování */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          3. Chování (rotace pro board layouty — plat / televize)
        </Typography>
        <Stack spacing={2}>
          {boardUsesRotation && (
            <TextField
              label="Rotace disciplín"
              type="number"
              size="small"
              value={draft.publicRotationSeconds}
              onChange={(e) =>
                setField(
                  'publicRotationSeconds',
                  Math.max(5, Math.min(120, Number(e.target.value) || 10)),
                )
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">sekund</InputAdornment>,
              }}
              sx={{ maxWidth: 220 }}
              helperText="5–120 s. Jak dlouho se na plátně drží jedna disciplína."
            />
          )}
          {boardUsesRotateOverall && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.publicRotateOverall}
                  onChange={(e) => setField('publicRotateOverall', e.target.checked)}
                />
              }
              label={
                draft.boardLayout === 'MATRIX'
                  ? 'Zobrazit buňky celkového pořadí'
                  : 'Zařadit celkové pořadí do rotace'
              }
            />
          )}
          {!boardSectionVisible && (
            <Typography variant="body2" color="text.secondary">
              Layout „{BOARD_LAYOUT_LABELS[draft.boardLayout]}" zobrazuje vše statisticky bez rotace — žádná nastavení rotace zde nejsou potřeba.
            </Typography>
          )}

          <TextField
            label="Auto-refresh interval"
            type="number"
            size="small"
            value={draft.publicAutoRefreshSeconds}
            onChange={(e) =>
              setField(
                'publicAutoRefreshSeconds',
                Math.max(5, Math.min(120, Number(e.target.value) || 15)),
              )
            }
            InputProps={{ endAdornment: <InputAdornment position="end">sekund</InputAdornment> }}
            sx={{ maxWidth: 220 }}
            helperText="5–120 s. Jak často FE provolá backend (fallback pro WebSocket). Platí pro veřejnou stránku i plat."
          />
        </Stack>
      </Box>

      <Divider />

      {/* Sekce D: Vzhled */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          4. Vzhled
        </Typography>
        <Stack spacing={2.5}>
          <FormControl>
            <FormLabel>Téma</FormLabel>
            <RadioGroup
              row
              value={draft.publicThemeMode}
              onChange={(e) => setField('publicThemeMode', e.target.value as PublicThemeMode)}
            >
              {THEMES.map((t) => (
                <FormControlLabel key={t} value={t} control={<Radio />} label={PUBLIC_THEME_LABELS[t]} />
              ))}
            </RadioGroup>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.publicShowLogo}
                onChange={(e) => setField('publicShowLogo', e.target.checked)}
              />
            }
            label="Zobrazit logo HasičiOnline"
          />
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.publicShowOrganizerLogo}
                  onChange={(e) => setField('publicShowOrganizerLogo', e.target.checked)}
                />
              }
              label="Zobrazit logo pořadatele"
            />
            {draft.publicShowOrganizerLogo && (
              <Stack spacing={1.5}>
                {/* Upload sekce */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    {hasUploadedLogo ? (
                      <Box
                        component="img"
                        src={logoSrc}
                        alt="Logo pořadatele"
                        sx={{
                          height: 48,
                          maxWidth: 200,
                          objectFit: 'contain',
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          px: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 48,
                          width: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'text.disabled',
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption">žádné logo</Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadMutation.isPending}
                      >
                        {hasUploadedLogo ? 'Nahrát nové' : 'Nahrát logo'}
                      </Button>
                      {hasUploadedLogo && (
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => deleteLogoMutation.mutate()}
                          disabled={deleteLogoMutation.isPending}
                          title="Smazat logo"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      hidden
                      onChange={handleFileSelected}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    PNG, JPG nebo WebP. Max. 3 MB. Doporučená výška 48 px.
                  </Typography>
                </Paper>

                {/* Nebo externí URL (fallback) */}
                <TextField
                  label="Nebo externí URL (volitelně)"
                  size="small"
                  placeholder="https://..."
                  value={draft.publicOrganizerLogoUrl}
                  onChange={(e) => setField('publicOrganizerLogoUrl', e.target.value)}
                  sx={{ maxWidth: 480 }}
                  helperText="Použije se jen pokud není nahraný soubor. Nahrané logo má přednost."
                />

                {/* Velikost loga */}
                <FormControl sx={{ maxWidth: 480, mt: 1 }}>
                  <FormLabel>Velikost loga pořadatele</FormLabel>
                  <Box sx={{ px: 1, pt: 0.5 }}>
                    <Slider
                      value={draft.publicOrganizerLogoScale}
                      onChange={(_, v) => setField('publicOrganizerLogoScale', v as number)}
                      min={50}
                      max={200}
                      step={10}
                      marks={[
                        { value: 50, label: '50 %' },
                        { value: 100, label: '100 %' },
                        { value: 150, label: '150 %' },
                        { value: 200, label: '200 %' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v} %`}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    100 % = výchozí velikost. Zvětšete pro výraznější logo na všech pohledech
                    (veřejná stránka, plat, spořič), zmenšete pokud se nevejde vedle textu.
                  </Typography>
                </FormControl>
              </Stack>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Disclaimer ve footeru veřejné stránky
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Zobrazí se jako oddělený box nad patičkou na stránce pro diváky (odkaz pro veřejnost).
          Typicky upozornění, že oficiální výsledek je jen to, co má pořadatel na papíře.
          Prázdné = disclaimer se nezobrazí.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label="Text disclaimeru (volitelné)"
          placeholder="Např. Výsledky jsou zobrazovány v rámci zkušebního provozu…"
          value={draft.publicDisclaimerText}
          onChange={(e) => setField('publicDisclaimerText', e.target.value.slice(0, 1000))}
          helperText={`${draft.publicDisclaimerText.length}/1000`}
          inputProps={{ maxLength: 1000 }}
        />
      </Box>

      <Divider />

      {/* Sekce E: Layout televize/plat */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          5. Layout pro plat / televizi / projektor
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Neveřejný displej na URL <code>/board/{competition.boardToken ?? '…'}</code>. Změna se
          projeví i na spuštěné stránce do{' '}
          {competition.publicAutoRefreshSeconds} s, při WebSocket aktualizaci hned.
        </Typography>
        {savedBoardHidden && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Původně zvolený board layout je nyní skrytý superadminem — bylo nastaveno „
            {BOARD_LAYOUT_LABELS[boardDefault]}". Uložte změny, aby se to propsalo do soutěže.
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          {visibleBoardLayouts.map((bl) => {
            const selected = draft.boardLayout === bl;
            return (
              <Paper
                key={bl}
                variant="outlined"
                onClick={() => setField('boardLayout', bl)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  transition: 'all 150ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <BoardLayoutPreview layout={bl} size={72} />
                <Typography fontWeight={700}>{BOARD_LAYOUT_LABELS[bl]}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {BOARD_LAYOUT_DESCRIPTIONS[bl]}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewBoard(bl);
                  }}
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                >
                  Ukázka
                </Button>
              </Paper>
            );
          })}
        </Box>

        <FormControl>
          <FormLabel>Barevné schéma boardu</FormLabel>
          <RadioGroup
            row
            value={draft.boardThemeMode}
            onChange={(e) => setField('boardThemeMode', e.target.value as PublicThemeMode)}
          >
            {THEMES.map((t) => (
              <FormControlLabel key={t} value={t} control={<Radio />} label={BOARD_THEME_LABELS[t]} />
            ))}
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            AUTO = světlé ve dne (6–20 h), tmavé v noci. Manuální volba přepíše automatiku.
          </Typography>
        </FormControl>

        <FormControl sx={{ mt: 3, maxWidth: 520 }}>
          <FormLabel>
            Velikost písma na boardu
            <Typography component="span" sx={{ ml: 1, color: 'primary.main', fontWeight: 700 }}>
              {draft.boardFontScale}%
            </Typography>
          </FormLabel>
          <Box sx={{ px: 1, pt: 1 }}>
            <Slider
              value={draft.boardFontScale}
              onChange={(_, v) => setField('boardFontScale', Array.isArray(v) ? v[0] : v)}
              min={50}
              max={200}
              step={5}
              marks={[
                { value: 50, label: '50%' },
                { value: 100, label: '100%' },
                { value: 150, label: '150%' },
                { value: 200, label: '200%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Ovlivní velikost všech textů na fullscreen displeji pro plat / televizi / projektor. Zvětšete pro lepší čitelnost ze sálu nebo na projekci z dálky.
          </Typography>
        </FormControl>

        {boardUsesTeamControls && (
          <FormControlLabel
            sx={{ mt: 3 }}
            control={
              <Checkbox
                checked={draft.boardShowAllTeams}
                onChange={(e) => setField('boardShowAllTeams', e.target.checked)}
              />
            }
            label={
              <LabelWithHelp
                label="Zobrazit všechna družstva (i bez výsledku)"
                help="Když je zapnuto, board ukáže kompletní startovní listinu včetně týmů, které ještě nezávodily — u nich se místo času zobrazí pomlčka. Hodí se pro pohled na postupnou registraci výsledků během soutěže. Vypnuto = jen top N týmů s výsledkem."
              />
            }
          />
        )}

        {boardUsesTeamControls && (
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Řazení družstev na boardu</FormLabel>
            <RadioGroup
              row
              value={draft.boardSortMode}
              onChange={(e) => setField('boardSortMode', e.target.value as BoardSortMode)}
            >
              {(['RANK', 'START_NUMBER'] as BoardSortMode[]).map((m) => (
                <FormControlLabel key={m} value={m} control={<Radio />} label={BOARD_SORT_MODE_LABELS[m]} />
              ))}
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              „Podle pořadí" = od nejrychlejšího (klasický scoreboard). „Podle startovního čísla" = v pořadí jak týmy startují (užitečné při zobrazení všech družstev jako kompletní listina).
            </Typography>
          </FormControl>
        )}

        {!boardUsesTeamControls && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Layout „{BOARD_LAYOUT_LABELS[draft.boardLayout]}" zobrazuje vždy všechna družstva seřazená podle startovního čísla.
          </Typography>
        )}

        {boardUsesLaneDetails && (
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={draft.boardShowLaneDetails}
                onChange={(e) => setField('boardShowLaneDetails', e.target.checked)}
              />
            }
            label={
              <LabelWithHelp
                label="Zobrazit časy jednotlivých drah"
                help="Pod úředním časem se ukáže čas levého a pravého proudu (PÚ) nebo první a druhé dráhy (Štafeta dvojic). Pro diváky je tak vidět detailní výsledek obou stran. Vypnuto = jen výsledný úřední čas."
              />
            }
          />
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Spořič před zahájením soutěže
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Dokud je soutěž ve stavu „Rozpracovaná" (DRAFT), plat zobrazí úvodní obrazovku s logem,
          QR kódem pro diváky, názvem, pořadatelem, místem, datem a volitelným textem. Po přepnutí
          soutěže na „Probíhá" (LIVE) se spořič automaticky schová a spustí se vybraný layout.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={draft.boardScreensaverEnabled}
              onChange={(e) => setField('boardScreensaverEnabled', e.target.checked)}
            />
          }
          label="Zobrazit spořič na platu před zahájením soutěže"
        />
        <TextField
          sx={{ mt: 1.5 }}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          label="Text na spořiči (volitelné)"
          placeholder="Např. Start soutěže 9:00"
          value={draft.boardCustomText}
          onChange={(e) => setField('boardCustomText', e.target.value.slice(0, 500))}
          helperText={`${draft.boardCustomText.length}/500 — zobrazí se pod metadaty soutěže. Lze použít víc řádků.`}
          inputProps={{ maxLength: 500 }}
          disabled={!draft.boardScreensaverEnabled}
        />
      </Box>

      <Divider />

      {/* Sekce F: Pravidla soutezi */}
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          6. Pravidla soutěže
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Nastavení obecných pravidel platná pro celou soutěž.
        </Typography>
        <TextField
          label="Práh falešných startů (po kolikátém = NP)"
          type="number"
          size="small"
          value={draft.falseStartThreshold}
          onChange={(e) => setField('falseStartThreshold', e.target.value)}
          helperText="1 = přísné (atletika 2010+), 2 = tolerantní"
          inputProps={{ min: 1, max: 5 }}
          sx={{ maxWidth: 320 }}
        />
      </Box>

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Link
          href={previewLink}
          target="_blank"
          rel="noopener"
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
        >
          Otevřít náhled v novém okně <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Link>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disabled={!isDirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? 'Ukládám…' : 'Uložit změny'}
        </Button>
      </Stack>

      {previewPublic && (
        <LayoutPreviewDialog
          kind="public"
          layout={previewPublic}
          open
          onClose={() => setPreviewPublic(null)}
        />
      )}
      {previewBoard && (
        <LayoutPreviewDialog
          kind="board"
          layout={previewBoard}
          open
          onClose={() => setPreviewBoard(null)}
        />
      )}
    </Stack>
  );
};
